import { Fragment, ReactNode, useMemo, useState } from 'react';
import { AlertTriangle, FileText, Scale, ShieldCheck } from 'lucide-react';
import offerMarkdown from '../../docs/legal/offer.md?raw';
import privacyMarkdown from '../../docs/legal/privacy.md?raw';
import consentMarkdown from '../../docs/legal/personal-data-consent.md?raw';
import '../styles/legal.css';

/**
 * Legal documents screen — view key `legal`.
 *
 * The audit found no privacy policy, no offer and no consent document anywhere
 * in the repository, which is a 152-ФЗ problem before it is a product problem.
 * The documents now live as markdown under `docs/legal/` — one source, versioned
 * with the code, reviewable in a diff — and this page renders them.
 *
 * They are drafts for a lawyer to finish, which is why every `{{PLACEHOLDER}}`
 * is rendered as a visible marker rather than quietly interpolated with
 * something plausible: an invented ИНН in a published policy is worse than an
 * obvious gap.
 *
 * The markdown is imported with Vite's `?raw`, so the text is part of the bundle
 * and the page works with no network call. `docs/` is copied into the frontend
 * build stage in Dockerfile and Dockerfile.prod for exactly this reason.
 */

export interface LegalDocument {
  id: 'offer' | 'privacy' | 'consent';
  title: string;
  short: string;
  icon: ReactNode;
  markdown: string;
  /** Path in the repository, shown so support can point a lawyer at the source. */
  source: string;
}

/**
 * Exported so the registration consent checkbox (another workstream) can link to
 * or embed the very same text instead of paraphrasing it.
 */
export const LEGAL_DOCUMENTS: LegalDocument[] = [
  {
    id: 'consent',
    title: 'Согласие на обработку персональных данных',
    short: 'Согласие',
    icon: <ShieldCheck size={17} aria-hidden="true" />,
    markdown: consentMarkdown,
    source: 'docs/legal/personal-data-consent.md',
  },
  {
    id: 'privacy',
    title: 'Политика обработки персональных данных',
    short: 'Политика',
    icon: <FileText size={17} aria-hidden="true" />,
    markdown: privacyMarkdown,
    source: 'docs/legal/privacy.md',
  },
  {
    id: 'offer',
    title: 'Публичная оферта',
    short: 'Оферта',
    icon: <Scale size={17} aria-hidden="true" />,
    markdown: offerMarkdown,
    source: 'docs/legal/offer.md',
  },
];

// ─── A deliberately small markdown subset ───────────────────────────────────
//
// No markdown dependency is added: the npm registry is unavailable in CI and
// there is no lockfile, so a new package would break the build for everyone.
// The documents are written against exactly the syntax handled here — headings,
// paragraphs, lists, pipe tables, blockquotes, rules, bold, inline code, links
// and {{PLACEHOLDER}} markers. Everything is built as React nodes, so no HTML is
// ever injected and there is no innerHTML anywhere in this file.

const INLINE = /(\{\{[A-Z0-9_]+\}\})|(\*\*[^*]+\*\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  INLINE.lastIndex = 0;
  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-i${index++}`;

    if (token.startsWith('{{')) {
      nodes.push(
        <mark className="legal-placeholder" key={key} title="Не заполнено: реквизит должен подставить юрист или оператор">
          {token.slice(2, -2)}
        </mark>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else {
      const label = token.slice(1, token.indexOf(']'));
      const href = token.slice(token.indexOf('](') + 2, -1);
      const external = /^https?:\/\//i.test(href);
      nodes.push(
        external
          ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
          // Relative links point at sibling documents; keep the reader on the page.
          : <span key={key} className="legal-internal-link">{label}</span>,
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function isTableSeparator(line: string): boolean {
  return /^\|?[\s:-]*-[\s|:-]*$/.test(line.trim()) && line.includes('-');
}

function splitRow(line: string): string[] {
  return line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
}

function renderBlocks(markdown: string, keyPrefix: string): ReactNode[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let cursor = 0;
  let blockIndex = 0;

  const nextKey = () => `${keyPrefix}-b${blockIndex++}`;

  while (cursor < lines.length) {
    const line = lines[cursor];
    const trimmed = line.trim();

    if (!trimmed) {
      cursor += 1;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push(<hr key={nextKey()} />);
      cursor += 1;
      continue;
    }

    // Heading
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length;
      const key = nextKey();
      const content = renderInline(heading[2], key);
      const Tag = (`h${Math.min(6, level + 1)}`) as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={key} className={`legal-h${level}`}>{content}</Tag>);
      cursor += 1;
      continue;
    }

    // Blockquote — the "draft, needs a lawyer" callouts. Rendered recursively so
    // the headings and lists inside them keep working.
    if (trimmed.startsWith('>')) {
      const collected: string[] = [];
      while (cursor < lines.length && lines[cursor].trim().startsWith('>')) {
        collected.push(lines[cursor].trim().replace(/^>\s?/, ''));
        cursor += 1;
      }
      const key = nextKey();
      const inner = collected.join('\n');
      const isWarning = /ЧЕРНОВИК|НЕ ПУБЛИКОВАТЬ|⚠/.test(inner);
      blocks.push(
        <blockquote key={key} className={isWarning ? 'legal-callout legal-callout-warning' : 'legal-callout'}>
          {isWarning && (
            <span className="legal-callout-icon" aria-hidden="true"><AlertTriangle size={18} /></span>
          )}
          <div>{renderBlocks(inner, key)}</div>
        </blockquote>,
      );
      continue;
    }

    // Pipe table
    if (trimmed.startsWith('|') && cursor + 1 < lines.length && isTableSeparator(lines[cursor + 1])) {
      const header = splitRow(trimmed);
      cursor += 2;
      const rows: string[][] = [];
      while (cursor < lines.length && lines[cursor].trim().startsWith('|')) {
        rows.push(splitRow(lines[cursor].trim()));
        cursor += 1;
      }
      const key = nextKey();
      blocks.push(
        <div className="legal-table-wrap" key={key}>
          <table>
            <thead>
              <tr>{header.map((cell, i) => <th key={`${key}-h${i}`}>{renderInline(cell, `${key}-h${i}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={`${key}-r${r}`}>
                  {row.map((cell, c) => <td key={`${key}-r${r}c${c}`}>{renderInline(cell, `${key}-r${r}c${c}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Ordered list
    if (/^\d+[.)]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (cursor < lines.length && /^\d+[.)]\s+/.test(lines[cursor].trim())) {
        items.push(lines[cursor].trim().replace(/^\d+[.)]\s+/, ''));
        cursor += 1;
        // Continuation lines of the same item.
        while (cursor < lines.length && /^\s{3,}\S/.test(lines[cursor]) && !/^\s*\d+[.)]\s+/.test(lines[cursor])) {
          items[items.length - 1] += ` ${lines[cursor].trim()}`;
          cursor += 1;
        }
      }
      const key = nextKey();
      blocks.push(<ol key={key}>{items.map((item, i) => <li key={`${key}-l${i}`}>{renderInline(item, `${key}-l${i}`)}</li>)}</ol>);
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (cursor < lines.length && /^[-*]\s+/.test(lines[cursor].trim())) {
        items.push(lines[cursor].trim().replace(/^[-*]\s+/, ''));
        cursor += 1;
        while (cursor < lines.length && /^\s{2,}\S/.test(lines[cursor]) && !/^\s*[-*]\s+/.test(lines[cursor])) {
          items[items.length - 1] += ` ${lines[cursor].trim()}`;
          cursor += 1;
        }
      }
      const key = nextKey();
      blocks.push(<ul key={key}>{items.map((item, i) => <li key={`${key}-l${i}`}>{renderInline(item, `${key}-l${i}`)}</li>)}</ul>);
      continue;
    }

    // Paragraph
    const paragraph: string[] = [];
    while (cursor < lines.length) {
      const current = lines[cursor].trim();
      if (!current) break;
      if (/^(#{1,6})\s+/.test(current) || current.startsWith('>') || current.startsWith('|')) break;
      if (/^[-*]\s+/.test(current) || /^\d+[.)]\s+/.test(current)) break;
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(current)) break;
      paragraph.push(current);
      cursor += 1;
    }
    if (paragraph.length) {
      const key = nextKey();
      blocks.push(<p key={key}>{renderInline(paragraph.join(' '), key)}</p>);
    }
  }

  return blocks;
}

/** Count of unfilled markers, so the page can state the state of the draft plainly. */
function countPlaceholders(markdown: string): number {
  return new Set(markdown.match(/\{\{[A-Z0-9_]+\}\}/g) || []).size;
}

export default function LegalPage() {
  const [activeId, setActiveId] = useState<LegalDocument['id']>(() => {
    const requested = new URLSearchParams(window.location.search).get('doc');
    return LEGAL_DOCUMENTS.some((document) => document.id === requested)
      ? (requested as LegalDocument['id'])
      : 'consent';
  });

  const active = LEGAL_DOCUMENTS.find((document) => document.id === activeId) || LEGAL_DOCUMENTS[0];
  const rendered = useMemo(() => renderBlocks(active.markdown, active.id), [active]);
  const placeholders = useMemo(() => countPlaceholders(active.markdown), [active]);

  return (
    <section className="view active legal-page">
      <header className="legal-hero">
        <div>
          <span className="eyebrow">Правовые документы</span>
          <h1>Оферта, политика и согласие</h1>
          <p>
            Тексты хранятся в репозитории вместе с кодом, поэтому любое изменение
            видно в истории. Открытая редакция ниже — черновик для юриста.
          </p>
        </div>
        <div className="legal-hero-meta">
          <span className="legal-hero-badge"><Scale size={20} aria-hidden="true" /></span>
          <div>
            <small>Источник</small>
            <strong>{active.source}</strong>
          </div>
        </div>
      </header>

      <div className="legal-banner" role="note">
        <AlertTriangle size={20} aria-hidden="true" />
        <div>
          <strong>Черновик, не прошедший юридическую проверку</strong>
          <p>
            Документы описывают, что программа делает с данными фактически, и не
            являются готовыми юридическими текстами. Публиковать их и собирать по
            ним согласия можно только после проверки юристом по 152-ФЗ.
            {placeholders > 0 && (
              <> В этом документе {placeholders} незаполненных реквизитов — они выделены цветом.</>
            )}
          </p>
        </div>
      </div>

      <nav className="legal-tabs" aria-label="Выбор документа">
        {LEGAL_DOCUMENTS.map((document) => (
          <button
            key={document.id}
            type="button"
            className={`legal-tab ${document.id === activeId ? 'active' : ''}`}
            aria-current={document.id === activeId ? 'page' : undefined}
            onClick={() => setActiveId(document.id)}
          >
            {document.icon}
            <span>{document.short}</span>
          </button>
        ))}
      </nav>

      <article className="legal-document">
        <h2 className="legal-document-title">{active.title}</h2>
        <Fragment key={active.id}>{rendered}</Fragment>
      </article>

      <footer className="legal-footer">
        <p>
          Вопросы по обработке данных, выгрузке и удалению — на адрес поддержки,
          указанный в документах. Выгрузить и удалить свои данные можно
          самостоятельно в разделе «Профиль».
        </p>
      </footer>
    </section>
  );
}
